import React, { useState, useRef } from 'react';
import Button from '../Button/Button';
import './UploadLogoModal.css';

interface UploadLogoModalProps {
    isOpen: boolean;
    onClose: () => void;
    onUpload: (file: File) => Promise<void>;
}

const ACCEPTED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
const ACCEPTED_LABEL = 'JPG · JPEG · PNG · GIF · WebP';
const MAX_SIZE = 2 * 1024 * 1024; // 2 Mo

const UploadLogoModal: React.FC<UploadLogoModalProps> = ({ isOpen, onClose, onUpload }) => {
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [preview, setPreview] = useState<string | null>(null);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isDragOver, setIsDragOver] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    React.useEffect(() => {
        if (!isOpen) {
            setSelectedFile(null);
            setPreview(null);
            setError(null);
            setIsDragOver(false);
        }
    }, [isOpen]);

    const validateFile = (file: File): string | null => {
        if (!ACCEPTED_TYPES.includes(file.type)) return `Format non accepté. Utilisez ${ACCEPTED_LABEL}.`;
        if (file.size > MAX_SIZE) return 'Le fichier est trop volumineux (max 2 Mo).';
        return null;
    };

    const applyFile = (file: File) => {
        const err = validateFile(file);
        if (err) { setError(err); setSelectedFile(null); setPreview(null); return; }
        setError(null);
        setSelectedFile(file);
        const reader = new FileReader();
        reader.onload = (e) => setPreview(e.target?.result as string);
        reader.readAsDataURL(file);
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) applyFile(file);
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
        setIsDragOver(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) setIsDragOver(false);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(false);
        const file = Array.from(e.dataTransfer.files).find(f => ACCEPTED_TYPES.includes(f.type));
        if (!file) { setError(`Format non accepté. Utilisez ${ACCEPTED_LABEL}.`); return; }
        applyFile(file);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedFile) { setError('Veuillez sélectionner un fichier'); return; }
        try {
            setUploading(true);
            setError(null);
            await onUpload(selectedFile);
            onClose();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Erreur lors de l'upload");
        } finally {
            setUploading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="upload-logo-modal-overlay" onClick={onClose}>
            <div className="upload-logo-modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="upload-logo-modal-header">
                    <h2>Logo entreprise</h2>
                    <button className="upload-logo-modal-close" onClick={onClose} type="button">×</button>
                </div>

                <form onSubmit={handleSubmit} className="upload-logo-form">
                    <div
                        className={`upload-logo-dropzone${isDragOver ? ' upload-logo-dropzone--over' : ''}${selectedFile ? ' upload-logo-dropzone--selected' : ''}`}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        onClick={() => !selectedFile && fileInputRef.current?.click()}
                        style={{ cursor: selectedFile ? 'default' : 'pointer' }}
                    >
                        {selectedFile && preview ? (
                            <>
                                <div className="upload-logo-preview">
                                    <img src={preview} alt="Aperçu" className="upload-logo-preview-img" />
                                </div>
                                <p className="upload-logo-filename">{selectedFile.name}</p>
                                <p className="upload-logo-size">{(selectedFile.size / 1024).toFixed(0)} Ko</p>
                                <button type="button" className="upload-logo-change" onClick={(e) => { e.stopPropagation(); setSelectedFile(null); setPreview(null); setError(null); }}>
                                    Changer d'image
                                </button>
                            </>
                        ) : (
                            <>
                                <div className="upload-logo-icon">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
                                    </svg>
                                </div>
                                <p className="upload-logo-label">Glissez votre logo ici</p>
                                <p className="upload-logo-sublabel">ou</p>
                                <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                                    <Button
                                        type="button"
                                        value="Parcourir"
                                        variant="primary"
                                        size="small"
                                        onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
                                        disabled={uploading}
                                        style={{ width: '25%' }}
                                    />
                                </div>
                                <p className="upload-logo-hint">{ACCEPTED_LABEL} &bull; max 2 Mo</p>
                            </>
                        )}
                    </div>

                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                        onChange={handleFileChange}
                        disabled={uploading}
                        style={{ display: 'none' }}
                    />

                    {error && <div className="upload-logo-error">{error}</div>}

                    <div className="upload-logo-actions">
                        <Button type="button" onClick={onClose} value="Annuler" variant="secondary" disabled={uploading} />
                        <Button type="submit" value={uploading ? 'Envoi en cours...' : 'Enregistrer'} disabled={uploading || !selectedFile} />
                    </div>
                </form>
            </div>
        </div>
    );
};

export default UploadLogoModal;
